import { ComponentType } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RouterLink } from '@angular/router';
import { APP_PATHS, INSTALLATION_DATA_SOURCE_POINT_COLUMNS, XEMEX_OID } from '@core/constants';
import { relativeAge } from '@core/helpers/relative-age.helper';
import { roleLabel } from '@core/helpers/role-label.helper';
import { installationZone, utcIso } from '@core/helpers/slot-series.helper';
import {
  DataSourceDevice,
  Installation,
  MeasurementPoint,
  getUserFeaturePermissions,
} from '@core/models';
import { InstallationProfileService } from '@core/services/installation-profile.service';
import { CardComponent } from '@custom-components/general/card';
import { TextButtonComponent, TonalButtonComponent } from '@custom-components/general/button';
import { ProgressBarComponent } from '@custom-components/general/progress-bar/progress-bar.component';
import { TableComponent, TableGroup } from '@custom-components/general/table/table.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import { InstallationDataSourcesStore } from '@root/app/store/installation-data-sources.store';
import { InstallationStore } from '@root/app/store/installation.store';
import { UserStore } from '@root/app/store/user.store';
import { AddPointRole, AddableRoles, addPointRoles, addableRoles } from '../../addable-roles';
import { DeviceStatusKind, deviceStatusKind } from '../../device-status';
import { PointAction, pointActions } from '../../point-actions';
import { ROLE_GROUP_ICONS, roleGroupKey } from '../../role-groups';
import {
  AddPointsDialogComponent,
  AddPointsDialogData,
} from '../add-points-dialog/add-points-dialog.component';
import {
  BindingDialogComponent,
  BindingDialogData,
} from '../binding-dialog/binding-dialog.component';
import { PointDialogComponent, PointDialogData } from '../point-dialog/point-dialog.component';
import {
  PointHistoryDialogComponent,
  PointHistoryDialogData,
} from '../point-history-dialog/point-history-dialog.component';
import {
  ProfileDialogComponent,
  ProfileDialogData,
} from '../profile-dialog/profile-dialog.component';
import { UnbindDialogComponent, UnbindDialogData } from '../unbind-dialog/unbind-dialog.component';

export interface PointRow extends Record<string, unknown> {
  point: MeasurementPoint;
  actions: PointAction[];
  // Shown under the role when it says more than the role does.
  pointName: string;
  role: string;
  required: string;
  unit: string;
  nature: string;
  state: string;
  stateTag: 'success' | 'warning';
  requiredTag: 'success-subtle';
  source: string;
  sourceChannel: string;
  deviceId: number | null;
  group: string;
}

// What every editor exposes, so the tab knows to read again after a refusal that found the point
// or the installation changed.
interface EditorDialog {
  readonly stale: Signal<boolean>;
}

export interface DeviceCard {
  deviceId: number;
  ldn: string;
  typeName: string | null;
  status: { kind: DeviceStatusKind; label: string } | null;
  boundChannels: number;
  channels: number;
  lastSeen: string | null;
}

const POINT_GROUPS: TableGroup[] = Object.entries(ROLE_GROUP_ICONS).map(([key, icon]) => ({
  key,
  label: `installations.roleGroups.${key}`,
  icon,
}));

const EDITOR_DIALOG_CONFIG: MatDialogConfig = {
  panelClass: 'no-padding-dialog',
  width: '40rem',
  maxWidth: 'calc(100vw - 2rem)',
};

@Component({
  selector: 'xemex-installation-data-sources-tab',
  imports: [
    CardComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    ProgressBarComponent,
    RouterLink,
    TableComponent,
    TextButtonComponent,
    TonalButtonComponent,
    TranslateModule,
  ],
  // Here rather than on the page, so the data sources are read only while this tab is shown.
  providers: [InstallationDataSourcesStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-data-sources-tab.component.html',
  styleUrl: './installation-data-sources-tab.component.scss',
})
export class InstallationDataSourcesTabComponent {
  installation = input.required<Installation>();

  readonly store = inject(InstallationDataSourcesStore);
  private readonly installationStore = inject(InstallationStore);
  private readonly profileService = inject(InstallationProfileService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);

  protected readonly APP_PATHS = APP_PATHS;
  protected readonly pointColumns = INSTALLATION_DATA_SOURCE_POINT_COLUMNS;
  protected readonly pointGroups = POINT_GROUPS;

  // The page replaces the installation after every write; only another installation reloads.
  private readonly installationOid = computed(() => this.installation().installationOid);
  private readonly timeZone = computed(() => installationZone(this.installation().timezone));

  private readonly permissions = computed(() =>
    getUserFeaturePermissions(this.userStore.user(), this.authStore.permissions(), XEMEX_OID),
  );
  private readonly installationActive = computed(() => this.installation().status === 'Active');
  readonly canEditBindings = computed(
    () => this.installationActive() && this.permissions().includes('edit_installation_bindings'),
  );
  readonly canEditProfile = computed(
    () =>
      this.installationActive() &&
      !!this.installation().profile &&
      this.permissions().includes('edit_installation_profile'),
  );
  private readonly canRetract = computed(
    () => this.installationActive() && this.permissions().includes('retract_installation_binding'),
  );

  // Read only for a role that may add points; the catalogue holds each role's multiplicity.
  private readonly roleCatalogue = rxResource({
    params: () => (this.canEditBindings() ? true : undefined),
    stream: () => this.profileService.roles(),
  });

  // Waits for the points too, or a single role that already has one would be offered.
  readonly addableRoles = computed<AddableRoles>(() => {
    const dataSources = this.store.dataSources();
    if (!this.roleCatalogue.hasValue() || dataSources === null) {
      return { inProfile: [], outsideProfile: [] };
    }
    return addableRoles(
      this.roleCatalogue.value(),
      this.installation().profile?.roles ?? [],
      dataSources.points,
    );
  });
  readonly addPointRoles = computed<AddPointRole[]>(() => {
    const dataSources = this.store.dataSources();
    if (!this.roleCatalogue.hasValue() || dataSources === null) return [];
    return addPointRoles(this.roleCatalogue.value(), this.addableRoles(), dataSources.points);
  });
  readonly canAddPoint = computed(() => {
    const { inProfile, outsideProfile } = this.addableRoles();
    return this.canEditBindings() && inProfile.length + outsideProfile.length > 0;
  });

  readonly pointRows = computed<PointRow[]>(() => {
    const access = {
      installationActive: this.installationActive(),
      canEditBindings: this.canEditBindings(),
    };
    return this.store.points().map(point => this.pointRow(point, pointActions(point, access)));
  });
  // A required role without a point yet still counts, as one point that is not bound.
  readonly requiredBound = computed<{ bound: number; total: number }>(() => {
    const required = this.store
      .points()
      .filter(point => point.required && point.state === 'active');
    const withPoint = new Set(required.map(point => point.role));
    const missing = (this.installation().profile?.roles ?? []).filter(
      role => role.required && role.state === 'active' && !withPoint.has(role.role),
    ).length;
    return {
      bound: required.filter(point => point.nature === 'derived' || point.bindingState === 'bound')
        .length,
      total: required.length + missing,
    };
  });
  readonly devices = computed(() => this.store.dataSources()?.devices ?? []);
  readonly deviceCards = computed<DeviceCard[]>(() => {
    const now = new Date();
    return this.devices().map(device => this.deviceCard(device, now));
  });

  constructor() {
    effect(() => {
      const installationOid = this.installationOid();
      untracked(() => this.store.load(installationOid));
    });
  }

  retry(): void {
    this.store.load(this.installationOid());
  }

  openAction(action: PointAction, point: MeasurementPoint): void {
    const installationOid = this.installationOid();
    const points = this.store.points();
    const timeZone = this.timeZone();
    switch (action) {
      case 'bind':
      case 'change':
        this.openEditor<BindingDialogComponent, BindingDialogData>(
          BindingDialogComponent,
          {
            installationOid,
            pointOid: point.pointOid,
            mode: action,
            timeZone,
            points,
            devices: this.devices(),
            unboundChannels: this.store.dataSources()?.unboundChannels ?? [],
          },
          { width: '48rem' },
        );
        return;
      case 'unbind':
        this.openEditor<UnbindDialogComponent, UnbindDialogData>(UnbindDialogComponent, {
          installationOid,
          pointOid: point.pointOid,
          timeZone,
          points,
        });
        return;
      case 'history':
        this.openEditor<PointHistoryDialogComponent, PointHistoryDialogData>(
          PointHistoryDialogComponent,
          {
            installationOid,
            pointOid: point.pointOid,
            timeZone,
            points,
            canRetract: this.canRetract(),
          },
        );
        return;
      case 'rename':
        this.openEditor<PointDialogComponent, PointDialogData>(PointDialogComponent, {
          installationOid,
          point,
        });
        return;
    }
  }

  // The save answers without points, so the installation is read again with the tab, which also
  // follows the roles the profile now asks for.
  editProfile(): void {
    const installation = this.installation();
    if (!installation.profile) return;
    this.openEditor<ProfileDialogComponent, ProfileDialogData>(
      ProfileDialogComponent,
      {
        installationOid: installation.installationOid,
        profile: installation.profile,
        points: this.store.dataSources()?.points ?? [],
      },
      { width: '60rem' },
    );
  }

  addPoint(): void {
    this.openEditor<AddPointsDialogComponent, AddPointsDialogData>(
      AddPointsDialogComponent,
      { installationOid: this.installationOid(), roles: this.addPointRoles() },
      { width: '48rem' },
    );
  }

  // The General points table and the Insights cards read the installation, so it is read again
  // with the tab after every write.
  private openEditor<C extends EditorDialog, D>(
    component: ComponentType<C>,
    data: D,
    config: MatDialogConfig = {},
  ): void {
    const ref = this.dialog.open<C, D, unknown>(component, {
      ...EDITOR_DIALOG_CONFIG,
      ...config,
      data,
    });
    const editor = ref.componentInstance;
    ref.afterClosed().subscribe(result => {
      if (result === undefined && !editor.stale()) return;
      this.store.reload();
      this.installationStore.loadDetail(this.installationOid());
    });
  }

  private pointRow(point: MeasurementPoint, actions: PointAction[]): PointRow {
    const source = point.binding?.source;
    const derived = point.nature === 'derived';
    const role = roleLabel(this.translate, point.role, point.roleDisplayName);
    const hidden =
      point.state === 'hidden'
        ? `(${this.translate.instant('installations.dataSources.hidden')})`
        : '';
    const pointName = [point.name !== role ? point.name : '', hidden].filter(Boolean).join(' ');
    return {
      point,
      actions,
      pointName,
      role,
      required: point.required ? this.translate.instant('installations.dataSources.required') : '',
      unit: point.unit,
      nature: this.translate.instant(`installations.nature.${point.nature}`),
      state: this.translate.instant(
        derived
          ? 'installations.nature.derived'
          : `installations.bindingState.${point.bindingState}`,
      ),
      stateTag: derived || point.bindingState === 'bound' ? 'success' : 'warning',
      requiredTag: 'success-subtle',
      source: source ? (source.ldn ?? String(source.deviceId)) : '',
      sourceChannel: source?.label ?? source?.valueName ?? '',
      deviceId: source?.deviceId ?? null,
      group: roleGroupKey(point.role),
    };
  }

  private deviceCard(device: DataSourceDevice, now: Date): DeviceCard {
    return {
      deviceId: device.deviceId,
      ldn: device.ldn ?? String(device.deviceId),
      typeName: device.deviceTypeName ?? device.protocol,
      status: this.deviceStatus(device.onlineStatus),
      boundChannels: device.boundChannelCount,
      channels: device.channelCount,
      lastSeen: this.lastSeen(device.lastSeenAt, now),
    };
  }

  private deviceStatus(onlineStatus: string | null | undefined): DeviceCard['status'] {
    const kind = deviceStatusKind(onlineStatus);
    return kind
      ? { kind, label: this.translate.instant(`installations.dataSources.device.${kind}`) }
      : null;
  }

  private lastSeen(lastSeenAt: string | null | undefined, now: Date): string | null {
    if (!lastSeenAt) return null;
    const ago = relativeAge(this.translate, new Date(utcIso(lastSeenAt)), now);
    return ago
      ? this.translate.instant('installations.dataSources.device.lastSeen', { ago })
      : this.translate.instant('installations.dataSources.device.lastSeenNow');
  }
}
